import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faCreditCard,
  faMobile,
} from '@fortawesome/free-solid-svg-icons';
import {
  HeaderRow,
  HeaderLeft,
  HeaderRight,
  PageTitle,
  PageSubtitle,
  TimeRangeSelect,
  StatsGrid,
  StatCard,
  StatCardBody,
  StatIconWrap,
  StatContent,
  StatValue,
  StatLabel,
  StatHelp,
} from './Styles';
import { PAGE_TITLE, PAGE_SUBTITLE, TIME_RANGE_OPTIONS, STAT_LABELS } from './constants';
import type { TimeRangeKey } from './store/adminAnalyticsSlice';
import type { OverviewSectionProps } from './types';
import SessionsDetail from './SessionsDetail';

const formatRevenue = (value: number): string => `$${value.toFixed(2)}`;

const OverviewSection: React.FC<OverviewSectionProps> = ({
  overview,
  activeSessions,
  merchantsWithSessions,
  timeRange,
  showSessionsDetails,
  onTimeRangeChange,
  onToggleSessions,
  onCloseSessions,
}) => {
  const totalRevenue = overview ? formatRevenue(overview.totalSales) : '$0.00';
  const totalTransactions = overview ? String(overview.totalOrders) : '0';

  return (
    <>
      <HeaderRow>
        <HeaderLeft>
          <PageTitle>{PAGE_TITLE}</PageTitle>
          <PageSubtitle>{PAGE_SUBTITLE}</PageSubtitle>
        </HeaderLeft>
        <HeaderRight>
          <TimeRangeSelect
            value={timeRange}
            onChange={(v) => onTimeRangeChange(v as TimeRangeKey)}
            options={TIME_RANGE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            size="middle"
          />
        </HeaderRight>
      </HeaderRow>
      <StatsGrid>
        <StatCard>
          <StatCardBody>
            <StatIconWrap $color="#22c55e">
              <FontAwesomeIcon icon={faDollarSign} />
            </StatIconWrap>
            <StatContent>
              <StatValue>{totalRevenue}</StatValue>
              <StatLabel>{STAT_LABELS.TOTAL_REVENUE}</StatLabel>
              <StatHelp $color="#22c55e">{STAT_LABELS.TOTAL_REVENUE_HELP}</StatHelp>
            </StatContent>
          </StatCardBody>
        </StatCard>
        <StatCard>
          <StatCardBody>
            <StatIconWrap $color="#3b82f6">
              <FontAwesomeIcon icon={faCreditCard} />
            </StatIconWrap>
            <StatContent>
              <StatValue>{totalTransactions}</StatValue>
              <StatLabel>{STAT_LABELS.TOTAL_TRANSACTIONS}</StatLabel>
              <StatHelp $color="#3b82f6">{STAT_LABELS.TOTAL_TRANSACTIONS_HELP}</StatHelp>
            </StatContent>
          </StatCardBody>
        </StatCard>
        <StatCard $selected onClick={onToggleSessions}>
          <StatCardBody>
            <StatIconWrap $color="#f59e0b">
              <FontAwesomeIcon icon={faMobile} />
            </StatIconWrap>
            <StatContent>
              <StatValue>{activeSessions}</StatValue>
              <StatLabel>{STAT_LABELS.ACTIVE_SESSIONS}</StatLabel>
              <StatHelp $color="#f59e0b">{STAT_LABELS.ACTIVE_SESSIONS_HELP}</StatHelp>
            </StatContent>
          </StatCardBody>
        </StatCard>
      </StatsGrid>
      {showSessionsDetails && (
        <SessionsDetail
          merchantsWithSessions={merchantsWithSessions}
          onClose={onCloseSessions}
        />
      )}
    </>
  );
};

export default React.memo(OverviewSection);
